/**
 * PATH: packages/ui/src/kanban/components/BoardInvitePicker.tsx
 * FILE_ID: UI-KANBAN-CREATE-INVITE-001
 * PURPOSE: Inline "invite specific teammates" picker used by the Create board
 *          dialog when visibility is "Members only". Search the org roster,
 *          add people with a per-invite role, see/remove the pending invites.
 * WHY: Operators creating a restricted ("structured") board want to nominate
 *      exactly who gets access up front — not create it, then hunt for the
 *      Access popover. Mirrors KanbanBoardMembersPopover's grant UX, but works
 *      on a pending list (no board exists yet); the parent grants on create.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 *   │ orgMembers + │ → │ filterCandidates │ → │ pending invites list │
 *   │ query        │   │ / addInvited     │   │ (userId + role)      │
 *   └──────────────┘   └──────────────────┘   └─────────────────────┘
 * DEPENDENCIES:
 *   - ../api: KanbanMember (roster shape, read-only)
 *   - ../utils/boardAccess: BOARD_ROLES, labels, BoardRole (single source)
 *   - ./KanbanAvatar: consistent member avatar
 */

import { useMemo, useState } from 'react'
import { UserPlus, X } from 'lucide-react'

import type { KanbanMember } from '../api'
import { BOARD_ROLES, BOARD_ROLE_LABELS, type BoardRole } from '../utils/boardAccess'
import { KanbanAvatar } from './KanbanAvatar'

/** A teammate the operator has nominated for the not-yet-created board. */
export interface InvitedMember {
  userId: number
  role: BoardRole
}

/** Upsert: add `userId` at `role`, or update the role if already invited. */
export function addInvited(
  list: readonly InvitedMember[],
  userId: number,
  role: BoardRole,
): InvitedMember[] {
  const idx = list.findIndex((m) => m.userId === userId)
  if (idx >= 0) {
    const next = list.slice()
    next[idx] = { userId, role }
    return next
  }
  return [...list, { userId, role }]
}

export function removeInvited(
  list: readonly InvitedMember[],
  userId: number,
): InvitedMember[] {
  return list.filter((m) => m.userId !== userId)
}

export function setInvitedRole(
  list: readonly InvitedMember[],
  userId: number,
  role: BoardRole,
): InvitedMember[] {
  return list.map((m) => (m.userId === userId ? { userId, role } : m))
}

/** Org roster minus already-invited people, filtered by a case-insensitive
 *  name/email query, capped so the dropdown stays light. */
export function filterCandidates(
  orgMembers: readonly KanbanMember[],
  invited: readonly InvitedMember[],
  query: string,
  limit = 12,
): KanbanMember[] {
  const taken = new Set(invited.map((m) => m.userId))
  const q = query.trim().toLowerCase()
  return orgMembers
    .filter((m) => !taken.has(m.id))
    .filter((m) =>
      q
        ? m.name.toLowerCase().includes(q) ||
          (m.email ?? '').toLowerCase().includes(q)
        : true,
    )
    .slice(0, limit)
}

export interface BoardInvitePickerProps {
  orgMembers: KanbanMember[]
  invited: InvitedMember[]
  onChange: (next: InvitedMember[]) => void
  disabled?: boolean
}

export function BoardInvitePicker({
  orgMembers,
  invited,
  onChange,
  disabled = false,
}: BoardInvitePickerProps) {
  const [query, setQuery] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [inviteRole, setInviteRole] = useState<BoardRole>('EDITOR')

  const candidates = useMemo(
    () => filterCandidates(orgMembers, invited, query),
    [orgMembers, invited, query],
  )
  const memberById = useMemo(() => {
    const map = new Map<number, KanbanMember>()
    for (const m of orgMembers) map.set(m.id, m)
    return map
  }, [orgMembers])

  return (
    <div data-testid="kb-board-invite" className="space-y-2">
      {/* Pending invite chips */}
      {invited.length > 0 && (
        <ul className="space-y-1">
          {invited.map((m) => {
            const member = memberById.get(m.userId)
            return (
              <li
                key={m.userId}
                className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <KanbanAvatar
                  member={{ id: m.userId, name: member?.name ?? `User ${m.userId}` }}
                  size={20}
                />
                <span className="min-w-0 flex-1 truncate text-[12px] text-neutral-800 dark:text-neutral-100">
                  {member?.name ?? `User ${m.userId}`}
                </span>
                <select
                  value={m.role}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(setInvitedRole(invited, m.userId, e.target.value as BoardRole))
                  }
                  aria-label={`Permission for ${member?.name ?? `user ${m.userId}`}`}
                  className="rounded border border-neutral-200 bg-white px-1 py-0.5 text-[10.5px] text-neutral-600 focus:border-blue-500 focus:outline-none disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
                >
                  {BOARD_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {BOARD_ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => onChange(removeInvited(invited, m.userId))}
                  disabled={disabled}
                  aria-label={`Remove ${member?.name ?? `user ${m.userId}`}`}
                  className="rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 disabled:opacity-60 dark:hover:bg-neutral-800"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {/* Add row: role select + searchable picker */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
          Invite as
        </span>
        <select
          value={inviteRole}
          disabled={disabled}
          onChange={(e) => setInviteRole(e.target.value as BoardRole)}
          aria-label="Permission for new invites"
          className="rounded border border-neutral-200 bg-white px-1 py-0.5 text-[10.5px] text-neutral-600 focus:border-blue-500 focus:outline-none disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
        >
          {BOARD_ROLES.map((r) => (
            <option key={r} value={r}>
              {BOARD_ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </div>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setPickerOpen(true)
          }}
          onFocus={() => setPickerOpen(true)}
          placeholder="Invite a teammate…"
          disabled={disabled}
          className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 pl-7 text-[12.5px] text-neutral-900 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
        <UserPlus className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-neutral-400" />
        {pickerOpen && candidates.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-44 overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
            {candidates.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onChange(addInvited(invited, u.id, inviteRole))
                  setQuery('')
                  setPickerOpen(false)
                }}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-900"
              >
                <KanbanAvatar member={{ id: u.id, name: u.name }} size={20} />
                <span className="truncate text-[12px] text-neutral-800 dark:text-neutral-100">
                  {u.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {orgMembers.length === 0 && (
        <p className="text-[11px] text-neutral-400">No teammates to invite yet.</p>
      )}
    </div>
  )
}
